import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { change, Field, reduxForm } from 'redux-form';

import Button from 'components/form/Button.component';
import { useTranslation, withTranslation } from 'providers/GILT.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import asObject from 'utils/objects/asObject.util';
import isEmptyString from 'utils/predicates/string/isEmptyString.util';
import parsfixed from 'utils/values/parsfixed.util';

import { addProduct, editProduct, resetSavedProperty } from 'settings/product-listing/actions';
import SavingStatus from 'settings/product-listing/components/SavingStatus';

import 'styles/common/confirmation-modal.scss';

const InputField = ({ input, meta, ...rest }) => {
  const { name, value } = asObject(input);
  const { touched, error, onFocus, active, form, dispatch } = asObject(meta);

  const className = `${touched && error && !active ? 'has-error' : ''}`;
  const resetField = () => void dispatch(change(form, name, ''));

  return (
    <div data-bem="InputField--settings">
      <div className="input-clear-container">
        <input className={`${className} utl-full-width`} onFocus={onFocus} {...input} {...rest} />

        {value !== '' && (
          // NOTE: This span clears the field, but it's not visible / no styles
          <span className="clear-value animated bounceIn" onClick={resetField} role="button" tabIndex="0" />
        )}
      </div>

      <div className="input-prompt-show">
        <div className="input-baseline" />
        <span>{touched ? error : ''}</span>
      </div>
    </div>
  );
};

const ProductFormWithSubmit = ({ addProduct, formType, editProduct, productId, salonId, cancel, reset, productsList, resetSavedProperty, handleSubmit, invalid, submitting }) => {
  const { t } = useTranslation();

  const onSubmit = useCallback(
    (values) => {
      const { title, brand, sub_brand: subBrand, price, barcode } = values;

      if (formType === 'edit') {
        return void editProduct(values, productId);
      }

      if (formType !== 'create') return;

      const quantity = parsfixed(values.quantityInStock);
      return void addProduct(salonId, title, brand, subBrand, price && parsfixed(price), quantity, barcode);
    },
    [addProduct, editProduct, formType, productId, salonId],
  );

  const { isPosting, isSaved } = asObject(productsList);

  const buttonName = useMemo(() => {
    if (isPosting) return t('common.saving');
    if (isSaved) return t('common.saved');

    return formType === 'edit' ? t('stocks.managerLayout.addProductModal.editItemButton') : t('stocks.managerLayout.addProductModal.addItemButton');
  }, [formType, isPosting, isSaved, t]);

  return (
    <div data-bem="ProductModal">
      <div className="modal-content">
        <div className="row form-horizontal">
          <div className="col-sm-7">
            <label htmlFor="title">{t('stocks.managerLayout.addProductModal.productName')}</label>
            <Field name="title" placeholder={t('stocks.managerLayout.addProductModal.productName')} type="text" component={InputField} />
          </div>

          <div className="col-sm-5">
            <label htmlFor="brand">{t('stocks.managerLayout.addProductModal.brand')}</label>
            <Field name="brand" type="text" component={InputField} />
          </div>

          <div className="col-sm-5">
            <label htmlFor="sub_brand">{t('stocks.managerLayout.addProductModal.subBrand')}</label>
            <Field name="sub_brand" type="text" component={InputField} />
          </div>

          <div className="col-sm-5">
            <label htmlFor="price">{t('stocks.managerLayout.addProductModal.retailPrice')}</label>
            <Field name="price" type="number" min="0" step="0.01" placeholder="0.00" component={InputField} />
          </div>

          <div className="col-sm-7">
            <label htmlFor="quantityInStock">{t('stocks.managerLayout.addProductModal.stockQuantity')}</label>
            <Field name="quantityInStock" type="number" min="0" placeholder={t('stocks.managerLayout.addProductModal.stockQuantity')} component={InputField} />
          </div>

          <div className="col-sm-7">
            <label htmlFor="barcode">{t('stocks.managerLayout.addProductModal.barcode')}</label>
            <Field name="barcode" type="text" component={InputField} />
          </div>

          <SavingStatus productsList={productsList} cancel={cancel} reset={reset} resetSavedProperty={resetSavedProperty} />

          <p className="add-product-notes" />
        </div>
      </div>

      <div className="modal-footer">
        <Button variant="outline" onClick={cancel}>
          {t('common.buttons.cancel')}
        </Button>

        <Button variant="primary" onClick={handleSubmit(onSubmit)} disabled={invalid || submitting || isPosting}>
          {buttonName}
        </Button>
      </div>
    </div>
  );
};

const validate = (values, props) => {
  const { t } = props;
  const errors = {};

  if (!values.title || fromAnyToTrimmed(values.title).length < 3) {
    errors.title = t('stocks.managerLayout.addProductModal.productNameErrMsg');
  }

  if (!values.brand || !fromAnyToTrimmed(values.brand)) {
    errors.brand = t('stocks.managerLayout.addProductModal.brandErrMsg');
  }

  if (isEmptyString(values.price)) {
    errors.price = t('stocks.managerLayout.addProductModal.priceRequiredErrMsg');
  } else if (isNaN(values.price)) {
    errors.price = t('stocks.managerLayout.addProductModal.priceInvalidErrMsg');
  } else if (parseFloat(values.price) < 0) {
    errors.price = t('stocks.managerLayout.addProductModal.pricePositiveErrMsg');
  }

  if (isEmptyString(values.quantityInStock)) {
    errors.quantityInStock = t('stocks.managerLayout.addProductModal.quantityRequiredErrMsg');
  } else if (isNaN(values.quantityInStock)) {
    errors.quantityInStock = t('stocks.managerLayout.addProductModal.quantityInvalidErrMsg');
  } else if (parseFloat(values.quantityInStock) < 0) {
    errors.quantityInStock = t('stocks.managerLayout.addProductModal.quantityPositiveErrMsg');
  }

  return errors;
};

const mapStateToProps = (state) => ({
  productsList: state?.settings?.products,
});

const mapDispatchToProps = (dispatch) => ({
  addProduct: (salonId, title, brand, sub_brand, price, barcode, quantityInStock) => dispatch(addProduct(salonId, title, brand, sub_brand, price, barcode, quantityInStock)),
  resetSavedProperty: () => dispatch(resetSavedProperty()),
  editProduct: (product, productId) => dispatch(editProduct(product, productId)),
});

const ProductFormWithReduxForm = reduxForm({
  validate,
  form: 'ProductForm',
})(ProductFormWithSubmit);

const ProductForm = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ProductFormWithReduxForm));

const ProductModal = ({ cancel, formType, popupTitle, product, productId, salonId }) => (
  <div data-bem="ProductModal">
    <div className="confirmation-modal animated zoomIn">
      <div className="modal-body">
        <h2 className="modal-title">{popupTitle}</h2>

        <ProductForm initialValues={product || null} productId={productId || null} cancel={cancel} salonId={salonId} formType={formType} />
      </div>
    </div>
  </div>
);

ProductModal.defaultProps = {
  product: null,
  productId: 0,
};

// this extra export is needed for tests that provide their own mock for connect()
export { InputField as InputField4Test };
export { ProductFormWithReduxForm as ProductForm4Test };

export default ProductModal;
